import { setError } from '../../../library/Core/SmartFunctions';
import { ControlOnChangeArguments, State } from '../../../library/Core/SmartTypes';

export const PRIMARY_JOB_ERROR = 'Only one current job can be marked as primary.';

const isOn = (value: any) => value === true || value === 1 || value === '1';

const asList = (value: any) => (Array.isArray(value) ? value : value ? [value] : []);

const anotherPrimaryExists = (state: State, formDataKey: string, rowIndex: number, currentJob: any) => {
    const formJobs = asList(state?.data?.[formDataKey]);
    const formIds = new Set(formJobs.map((job) => job?.id).filter((id) => id != null && id !== ''));

    const anotherInForm = formJobs.some((job, index) => {
        if (Number.isInteger(rowIndex) && index === rowIndex) return false;
        if (currentJob?.id != null && job?.id === currentJob.id) return false;
        if (!Number.isInteger(rowIndex) && formJobs.length === 1) return false;
        return isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent);
    });

    const anotherSaved = asList(state?.data?.jobsList).some((job) => {
        if (currentJob?.id != null && job?.id === currentJob.id) return false;
        if (formIds.has(job?.id)) return false;
        return isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent);
    });

    return anotherInForm || anotherSaved;
};

export function onPrimaryCurrentChange(formDataKey: string) {
    return (props: ControlOnChangeArguments) => {
        if (!isOn(props.value)) {
            setError(props.dataKey, [], props.dispatch);
            return;
        }

        const rowIndex = Number(String(props.parentDataKey ?? '').split('.').pop());
        if (!anotherPrimaryExists(props.state, formDataKey, rowIndex, props.row)) {
            setError(props.dataKey, [], props.dispatch);
            return;
        }

        props.errorMessages?.push(PRIMARY_JOB_ERROR);
        window.setTimeout(() => {
            props.dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: props.dataKey, value: false, errorMessages: [PRIMARY_JOB_ERROR] },
            });
            props.dispatch({ type: 'SHOW_ERRORS' });
        }, 0);
    };
}

/** Primary was turned back off, so a leftover message must not block the next save. */
export function primaryErrorKeysToIgnore(state: State, formDataKey: string) {
    const multiple = Array.isArray(state?.data?.[formDataKey]);
    return asList(state?.data?.[formDataKey]).flatMap((job, index) => {
        if (isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent)) return [];
        const key = multiple ? `${formDataKey}.${index}.isPrimaryCurrent` : `${formDataKey}.isPrimaryCurrent`;
        return [key];
    });
}

export function validateOnePrimaryCurrent(state: State, dispatch: (dispatchEvent: any) => void, formDataKey: string) {
    const formJobs = asList(state?.data?.[formDataKey]).map((job) => ({
        ...job,
        isPrimaryCurrent: isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent),
    }));
    const formIds = new Set(formJobs.map((job) => job?.id).filter((id) => id != null && id !== ''));
    const otherJobs = asList(state?.data?.jobsList).filter((job) => !formIds.has(job?.id));
    const primaryCount = [...formJobs, ...otherJobs].filter((job) => isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent)).length;
    const multiple = Array.isArray(state?.data?.[formDataKey]);

    asList(state?.data?.[formDataKey]).forEach((job, index) => {
        const key = multiple ? `${formDataKey}.${index}.isPrimaryCurrent` : `${formDataKey}.isPrimaryCurrent`;
        if (primaryCount > 1 && isOn(job?.isCurrent) && isOn(job?.isPrimaryCurrent)) {
            setError(key, [PRIMARY_JOB_ERROR], dispatch);
        } else {
            setError(key, [], dispatch);
        }
    });

    return primaryCount <= 1;
}
